import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import Hero from './components/Hero';
import RegisterForm from './components/RegisterForm';
import LoginForm from './components/LoginForm';
import EmailVerification from './components/EmailVerification';
// import ProfileCompletion from './components/ProfileCompletion';
import PasswordSetup from './components/PasswordSetup';
import ResetPassword from './components/ResetPassword';
import CompleteEmployerProfile from './components/CompleteEmployerProfile';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';
import LearnMore from './components/LearnMore';
import EnquiryForm from './components/EnquiryForm';
import UnifiedRoutes from './components/UnifiedRoutes';
import OrganizationSetupModal from './components/OrganizationSetupModal';
import ToastContainer from './components/ToastContainer';
import CISCalculator from './components/CISCalculator';
import AccountantsPage from './components/AccountantsPage';
import PricingPage from './components/PricingPage';
import BlogPage from './components/BlogPage';
import HelmetVerification from './components/HelmetVerification';
import API_CONFIG from './utils/apiConfig';
import { requiresSubcontractorInviteWizard } from './utils/subcontractorInviteOnboardingGate';
import './App.css';
import { DashboardNavProvider, isAppDashboardRoute, isLoginOrSignupRoute } from './context/DashboardNavContext';

// Scroll to top when route changes (e.g. footer links)
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // Prevent browser from restoring previous scroll position on navigation
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    scrollToTop();
    // Run again after paint and after next tick so we win over any layout/restoration
    const id = requestAnimationFrame(() => {
      scrollToTop();
      setTimeout(scrollToTop, 0);
    });
    return () => cancelAnimationFrame(id);
  }, [pathname]);
  return null;
};

/** No marketing footer on dashboard / unified app routes or login & sign-up */
const ConditionalFooter = ({ user }) => {
  const { pathname } = useLocation();
  if (isAppDashboardRoute(pathname) || isLoginOrSignupRoute(pathname)) return null;
  return <Footer user={user} />;
};

/** No global header on login & sign-up */
const ConditionalHeader = ({ user, onLogout }) => {
  const { pathname } = useLocation();
  const isSuperAdminUser =
    user && (user.role === 'superuser' || user.role === 'super_admin');
  const hideForSuperAdminDashboard = Boolean(isSuperAdminUser && isAppDashboardRoute(pathname));
  if (isLoginOrSignupRoute(pathname) || hideForSuperAdminDashboard) return null;
  return <Header user={user} onLogout={onLogout} />;
};

/** Login / sign-up: no header offset — padding-top comes from App.css default otherwise */
const AppMain = ({ user, modalBlocksInteraction, children }) => {
  const { pathname } = useLocation();
  const authFullBleed = isLoginOrSignupRoute(pathname);
  const isSuperAdminUser =
    user && (user.role === 'superuser' || user.role === 'super_admin');
  const superAdminDashboardShell = Boolean(isSuperAdminUser && isAppDashboardRoute(pathname));
  const mainClassName = authFullBleed
    ? 'main--auth-full-bleed'
    : superAdminDashboardShell
      ? 'main--superadmin-dashboard-shell'
      : undefined;
  return (
    <main
      className={mainClassName}
      style={modalBlocksInteraction ? { pointerEvents: 'none', userSelect: 'none' } : undefined}
    >
      {children}
    </main>
  );
};

// HMRC OAuth Callback Handler Component
// HMRC redirects to /hmrc/callback (frontend route) to avoid conflicts with /api/* routes
// This component then redirects to the backend endpoint to complete the OAuth flow
const HMRCCallbackHandler = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const queryString = location.search;
    
    // Always redirect to backend callback endpoint
    // The backend route is at /api/v1/unified-auth/hmrc/callback
    const backendCallbackUrl = `${API_CONFIG.BASE_URL}/api/v1/unified-auth/hmrc/callback${queryString}`;
    window.location.href = backendCallbackUrl;
  }, [location.search]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #2a4252 0%, #1e2a35 50%, #2a4252 100%)',
      color: 'white'
    }}>
      <div style={{
        width: '50px',
        height: '50px',
        border: '4px solid rgba(255, 255, 255, 0.2)',
        borderTop: '4px solid #57bedd',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
      }}></div>
      <p>Processing HMRC connection...</p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Simple Unauthorized component
const Unauthorized = () => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center', 
    justifyContent: 'center', 
    minHeight: 'calc(100vh - 80px)',
    background: 'linear-gradient(135deg, #2a4252 0%, #1e2a35 50%, #2a4252 100%)',
    color: 'white',
    textAlign: 'center',
    padding: '40px 20px',
    marginTop: '24px'
  }}>
    <h1 style={{ fontSize: '48px', marginBottom: '20px', color: '#dc3545' }}>🚫</h1>
    <h2 style={{ fontSize: '32px', marginBottom: '20px', color: '#dc3545' }}>Unauthorized Access</h2>
    <p style={{ fontSize: '18px', marginBottom: '30px', opacity: 0.9 }}>
      You don't have permission to access this page.
    </p>
    <button 
      onClick={() => window.location.href = '/'}
      style={{
        background: 'linear-gradient(135deg, #57bedd, #57bedd)',
        color: 'white',
        border: 'none',
        padding: '15px 30px',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
    >
      Go to Home
    </button>
  </div>
);

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Make API_CONFIG.fetchWithAuth available globally for backward compatibility
  useEffect(() => {
    window.fetchWithAuth = API_CONFIG.fetchWithAuth;
  }, []);

  // Function to load user data
  const loadUserData = async () => {
    const token = localStorage.getItem('access_token');
    
    if (token) {
      try {
        // Fetch current user info with company details
        const response = await API_CONFIG.fetchWithAuth(API_CONFIG.getApiUrl(API_CONFIG.endpoints.me));
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        } else {
          throw new Error('Failed to fetch user info');
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        // Clear invalid token
        localStorage.removeItem('access_token');
        localStorage.removeItem('token_type');
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUserData();
  }, []);

  // Listen for storage changes and custom events
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user' && e.newValue) {
        try {
          const userData = JSON.parse(e.newValue);
          setUser(userData);
        } catch (err) {
          console.error('Error parsing user data from storage:', err);
        }
      }
    };

    const handleUserUpdated = (e) => {
      // When 'user-updated' event is triggered, reload user from localStorage
      const userJson = localStorage.getItem('user');
      if (userJson) {
        try {
          const userData = JSON.parse(userJson);
          setUser(userData);
        } catch (err) {
          console.error('Error parsing user data:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('user-updated', handleUserUpdated);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('user-updated', handleUserUpdated);
    };
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isSuperAdminUser =
    user && (user.role === 'superuser' || user.role === 'super_admin');

  const needsOrgBootstrap = Boolean(
    user &&
      !isSuperAdminUser &&
      (!user.organization ||
        !String(user.organization.organization_name || '').trim() ||
        !String(user.organization.business_type || '').trim())
  );

  const needsNameBootstrap = Boolean(
    user &&
      !isSuperAdminUser &&
      (!String(user.first_name || '').trim() || !String(user.surname || '').trim())
  );

  const subInviteWizardActive = Boolean(user && requiresSubcontractorInviteWizard(user));

  const showOrganizationSetupModal =
    (needsOrgBootstrap || needsNameBootstrap) && !subInviteWizardActive;

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2a4252 0%, #1e2a35 50%, #2a4252 100%)',
        color: 'white'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(255, 255, 255, 0.2)',
          borderTop: '4px solid #57bedd',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <p>Loading...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <HelmetProvider> 
    <Router>
      <DashboardNavProvider>
      <ScrollToTop />
      <div className="App">
        <HelmetVerification />
        {showOrganizationSetupModal ? (
          <OrganizationSetupModal
            user={user}
            requireOrgSetup={needsOrgBootstrap}
            onCompleted={async () => {
              await loadUserData();
            }}
          />
        ) : null}
        <ConditionalHeader user={user} onLogout={handleLogout} />
        <AppMain user={user} modalBlocksInteraction={showOrganizationSetupModal}>
          <Routes>
          <Route path="/" element={<Hero />} />
          <Route path="/register" element={<RegisterForm />} />
          <Route path="/login" element={<LoginForm onLoginSuccess={handleLoginSuccess} />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          {/* <Route path="/complete-profile" element={<ProfileCompletion />} /> */}
          <Route path="/setup-password" element={<PasswordSetup />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/complete-employer-profile" element={<CompleteEmployerProfile />} />
          <Route path="/register-subcontractor" element={<RegisterForm />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/learn-more" element={<LearnMore />} />
          <Route path="/contact" element={<EnquiryForm />} />
          <Route path="/enquiry" element={<EnquiryForm />} />

          {/* Add CIS Calculator route here - public page */}
          <Route path="/cis-calculator" element={<CISCalculator />} />

          <Route path="/pricing" element={<PricingPage />} />

          <Route path="/accountants" element={<AccountantsPage />} />
         
          <Route path="/blog" element={<BlogPage />}  />

          {/* HMRC OAuth Callback Handler */}
          <Route 
            path="/hmrc/callback" 
            element={<HMRCCallbackHandler />} 
          />
          
          {/* Unified Routes - Handle all authenticated paths */}
          <Route 
            path="/dashboard" 
            element={user ? <UnifiedRoutes user={user} onLogout={handleLogout} onUserRefresh={loadUserData} /> : <Navigate to="/login" />} 
          />
          <Route
            path="/dashboard/employer"
            element={user ? <UnifiedRoutes user={user} onLogout={handleLogout} onUserRefresh={loadUserData} /> : <Navigate to="/login" />}
          />
          <Route
            path="/dashboard/subcontractor"
            element={user ? <UnifiedRoutes user={user} onLogout={handleLogout} onUserRefresh={loadUserData} /> : <Navigate to="/login" />}
          />
          <Route 
            path="/subcontractor/dashboard" 
            element={user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/subcontractors" 
            element={user ? <UnifiedRoutes user={user} onLogout={handleLogout} onUserRefresh={loadUserData} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/applications" 
            element={user ? <UnifiedRoutes user={user} onLogout={handleLogout} onUserRefresh={loadUserData} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/reports" 
            element={user ? <UnifiedRoutes user={user} onLogout={handleLogout} onUserRefresh={loadUserData} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/settings" 
            element={user ? <UnifiedRoutes user={user} onLogout={handleLogout} onUserRefresh={loadUserData} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/application" 
            element={user ? <UnifiedRoutes user={user} onLogout={handleLogout} onUserRefresh={loadUserData} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/profile" 
            element={user ? <UnifiedRoutes user={user} onLogout={handleLogout} onUserRefresh={loadUserData} /> : <Navigate to="/login" />} 
          />
          <Route
            path="/profile/organization"
            element={user ? <UnifiedRoutes user={user} onLogout={handleLogout} onUserRefresh={loadUserData} /> : <Navigate to="/login" />}
          />
          <Route
            path="/profile/users"
            element={user ? <UnifiedRoutes user={user} onLogout={handleLogout} onUserRefresh={loadUserData} /> : <Navigate to="/login" />}
          />
          </Routes>
        </AppMain>
        <ConditionalFooter user={user} />
        <ToastContainer />
      </div>
      </DashboardNavProvider>
    </Router>
    </HelmetProvider>
  );
}

export default App;